import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  evaluateDeviceHealth,
  FPS_WARMUP_MS,
  getPowerHealth,
  getThermalState,
  mapDeviceHealthReasons,
  type DeviceHealthEvaluation,
  type DeviceHealthMessage,
  type DeviceHealthSnapshot,
  type RppgAbortReasonCode,
  type RppgScanAbortSource,
} from 'lib/medsi/device-health';
import { useCameraInterrupted } from 'hooks/medsi/useCameraInterrupted';
import { useDeliveredFps } from 'hooks/medsi/useDeliveredFps';
import { useDeviceHealthSensors } from 'hooks/medsi/useDeviceHealthSensors';
import { useScanWakeLock } from 'hooks/medsi/useScanWakeLock';

export interface MedsiDeviceHealthAbort {
  reasons: RppgAbortReasonCode[];
  source: RppgScanAbortSource;
  messages: DeviceHealthMessage[];
}

export interface UseMedsiRuntimeHealthResult {
  snapshot: DeviceHealthSnapshot;
  blockingReasons: RppgAbortReasonCode[];
  isHealthy: boolean;
  healthAbort: MedsiDeviceHealthAbort | null;
  clearHealthAbort: () => void;
  assertHealthyForStart: () => Promise<DeviceHealthEvaluation>;
  /** Call when a mid-scan / idle health failure should surface the alert modal. */
  presentHealthAbort: (
    reasons: RppgAbortReasonCode[],
    source: RppgScanAbortSource,
  ) => void;
}

export function useMedsiRuntimeHealth(options: {
  videoRef: RefObject<HTMLVideoElement | null>;
  isScanning: boolean;
  isProcessing?: boolean;
}): UseMedsiRuntimeHealthResult {
  const { videoRef, isScanning, isProcessing = false } = options;

  const { thermalState, lowPowerMode, batteryLevel, refresh } =
    useDeviceHealthSensors();
  const deliveredFps = useDeliveredFps(videoRef, isScanning);
  const cameraInterrupted = useCameraInterrupted(videoRef, isScanning);
  const [healthAbort, setHealthAbort] = useState<MedsiDeviceHealthAbort | null>(
    null,
  );
  const hasPresentedIdleBlockRef = useRef(false);
  const scanStartedAtRef = useRef<number | null>(null);

  useScanWakeLock(isScanning || isProcessing);

  useEffect(() => {
    if (isScanning) {
      scanStartedAtRef.current = Date.now();
    } else {
      scanStartedAtRef.current = null;
    }
  }, [isScanning]);

  const snapshot: DeviceHealthSnapshot = useMemo(
    () => ({
      batteryLevel,
      lowPowerMode,
      thermalState,
      deliveredFps,
      cameraInterrupted,
    }),
    [batteryLevel, lowPowerMode, thermalState, deliveredFps, cameraInterrupted],
  );

  const startEval = useMemo(
    () =>
      evaluateDeviceHealth(snapshot, {
        includeFps: false,
        includeCameraInterrupt: false,
      }),
    [snapshot],
  );

  const presentHealthAbort = useCallback(
    (reasons: RppgAbortReasonCode[], source: RppgScanAbortSource) => {
      setHealthAbort({
        reasons,
        source,
        messages: mapDeviceHealthReasons(reasons),
      });
    },
    [],
  );

  const clearHealthAbort = useCallback(() => {
    setHealthAbort(null);
    hasPresentedIdleBlockRef.current = false;
  }, []);

  const assertHealthyForStart =
    useCallback(async (): Promise<DeviceHealthEvaluation> => {
      let power;
      let freshThermal: typeof thermalState;

      try {
        const refreshed = await refresh();
        power = refreshed.power;
        freshThermal = refreshed.thermalState;
      } catch {
        const [fallbackPower, fallbackThermal] = await Promise.all([
          getPowerHealth(),
          getThermalState(),
        ]);
        power = fallbackPower;
        freshThermal = fallbackThermal;
      }

      return evaluateDeviceHealth(
        {
          batteryLevel: power.batteryLevel,
          lowPowerMode: power.lowPowerMode,
          thermalState: freshThermal,
          deliveredFps: null,
          cameraInterrupted: false,
        },
        {
          includeFps: false,
          includeCameraInterrupt: false,
        },
      );
    }, [refresh, thermalState]);

  // Mid-scan: battery / thermal / LPM (when available).
  useEffect(() => {
    if (!isScanning || healthAbort) return;

    const evaluation = evaluateDeviceHealth(snapshot, {
      includeFps: false,
      includeCameraInterrupt: false,
    });
    if (!evaluation.healthy) {
      presentHealthAbort(evaluation.reasons, 'device_health');
    }
  }, [
    isScanning,
    healthAbort,
    snapshot,
    presentHealthAbort,
  ]);

  // Mid-scan: camera track interruption.
  useEffect(() => {
    if (!isScanning || healthAbort || !cameraInterrupted) return;
    presentHealthAbort(['CAMERA_INTERRUPTED'], 'camera_interrupt');
  }, [isScanning, healthAbort, cameraInterrupted, presentHealthAbort]);

  // Mid-scan: low delivered FPS after warmup.
  useEffect(() => {
    if (!isScanning || healthAbort) return;
    if (deliveredFps == null) return;

    const startedAt = scanStartedAtRef.current;
    if (startedAt == null || Date.now() - startedAt < FPS_WARMUP_MS) {
      return;
    }

    const evaluation = evaluateDeviceHealth(
      { ...snapshot, deliveredFps },
      { includeFps: true, includeCameraInterrupt: false },
    );
    if (!evaluation.healthy && evaluation.reasons.includes('LOW_FRAME_RATE')) {
      presentHealthAbort(['LOW_FRAME_RATE'], 'low_fps');
    }
  }, [isScanning, healthAbort, deliveredFps, snapshot, presentHealthAbort]);

  // Idle: surface blocking health (e.g. low battery) once via the same modal.
  useEffect(() => {
    if (isScanning || healthAbort) return;

    if (startEval.healthy) {
      hasPresentedIdleBlockRef.current = false;
      return;
    }

    if (hasPresentedIdleBlockRef.current || startEval.reasons.length === 0) {
      return;
    }

    hasPresentedIdleBlockRef.current = true;
    presentHealthAbort(startEval.reasons, 'device_health');
  }, [isScanning, healthAbort, startEval, presentHealthAbort]);

  return {
    snapshot,
    blockingReasons: startEval.reasons,
    isHealthy: startEval.healthy,
    healthAbort,
    clearHealthAbort,
    assertHealthyForStart,
    presentHealthAbort,
  };
}
